import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Clock, Calendar, CheckCircle, X, MapPin, ArrowRight, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import InquiryForm from '../components/InquiryForm';
import SEO, { breadcrumbSchema, faqSchema, touristTripSchema } from '../components/SEO';
import { destinationPagesData } from '../data/detailPages';

const DestinationDetail = () => {
  const { slug } = useParams();
  const data = destinationPagesData[slug];

  if (!data) return <Navigate to="/tour-packages" replace />;

  const schemas = [
    breadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Tour Packages', path: '/tour-packages' },
      { name: data.name, path: `/destinations/${slug}` }
    ]),
    touristTripSchema(data.fullName, data.overview, data.name),
    faqSchema(data.faqs)
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title={data.seoTitle}
        description={data.seoDescription}
        keywords={data.seoKeywords}
        path={`/destinations/${slug}`}
        image={data.heroImage}
        schema={schemas}
      />

      {/* Hero */}
      <section
        className="relative min-h-[460px] flex items-end bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(to top, rgba(0,0,0,0.8), rgba(0,0,0,0.2)), url('${data.heroImage}')`
        }}
      >
        <div className="container mx-auto px-4 py-12 text-white">
          <nav className="text-sm text-white/80 mb-4">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/tour-packages" className="hover:text-white">Tour Packages</Link>
            <span className="mx-2">/</span>
            <span>{data.name}</span>
          </nav>
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-3">{data.fullName}</h1>
          <p className="text-lg text-white/90 max-w-3xl mb-6">{data.overview}</p>
          <div className="flex flex-wrap gap-6 text-sm">
            <span className="flex items-center gap-2"><Calendar size={16} /> {data.duration}</span>
            <span className="flex items-center gap-2"><Clock size={16} /> Best time: {data.bestTime}</span>
            <span className="flex items-center gap-2"><MapPin size={16} /> Starting from {data.price}</span>
          </div>
        </div>
      </section>

      {/* Main */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-12">
              {/* Highlights */}
              <div>
                <h2 className="text-3xl font-bold mb-6 text-gray-900">Tour Highlights</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {data.highlights.map((h, i) => (
                    <div key={i} className="flex items-start gap-2 bg-gradient-to-br from-blue-50 to-orange-50 p-4 rounded-lg">
                      <CheckCircle size={20} className="text-[#F7945D] mt-0.5 flex-shrink-0" />
                      <span className="text-gray-800">{h}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Itinerary */}
              <div>
                <h2 className="text-3xl font-bold mb-6 text-gray-900">Day-wise Itinerary</h2>
                <div className="space-y-4">
                  {data.itinerary.map((d, i) => (
                    <Card key={i} className="overflow-hidden">
                      <CardContent className="p-0 flex">
                        <div className="bg-gradient-to-b from-[#2D8DB4] to-[#F7945D] text-white p-4 md:p-5 w-24 md:w-32 flex-shrink-0 flex items-center justify-center font-bold text-sm md:text-base">
                          {d.day}
                        </div>
                        <div className="p-5">
                          <h4 className="font-bold text-lg mb-1 text-gray-900">{d.title}</h4>
                          <p className="text-gray-600 text-sm">{d.desc}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Inclusions / Exclusions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-4 text-[#2D8DB4]">What's Included</h3>
                    <ul className="space-y-2">
                      {data.inclusions.map((it, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <CheckCircle size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-4 text-[#F7945D]">Not Included</h3>
                    <ul className="space-y-2">
                      {data.exclusions.map((it, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <X size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>

              {/* FAQ */}
              <div>
                <h2 className="text-3xl font-bold mb-6 text-gray-900">Frequently Asked Questions</h2>
                <Accordion type="single" collapsible className="w-full">
                  {data.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`item-${i}`}>
                      <AccordionTrigger className="text-left font-semibold">{f.q}</AccordionTrigger>
                      <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>

            {/* Sticky Inquiry */}
            <aside className="lg:sticky lg:top-28 self-start">
              <InquiryForm
                inquiryType="tour"
                defaultSubject={`${data.name} Tour Inquiry`}
                defaultDetails={{ destination: data.name, page: `/destinations/${slug}` }}
                title={`Book ${data.name} Tour`}
                subtitle="Tailored itineraries. No hidden charges."
              />
            </aside>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-gradient-to-r from-[#2D8DB4] to-[#F7945D] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-3">Plan your dream {data.name} trip with us</h2>
          <p className="mb-6">Customizable packages | Best price guarantee | 24/7 support</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/tour-packages">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#2D8DB4]">
                See All Destinations <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
            <a href="tel:+919016930091">
              <Button size="lg" className="bg-white text-[#2D8DB4] hover:bg-gray-100">
                <Phone size={18} className="mr-2" /> Call +91 90169 30091
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DestinationDetail;
